import { KeyRound, Lock } from 'lucide-react';
import { useState } from 'react';

import { mensagemDeErro } from '@/lib/erros';
import { CampoSenha } from '@/ui/components/campo-senha';
import { Dropzone } from '@/ui/components/dropzone';
import { toast } from '@/ui/primitives/aviso';
import { Button } from '@/ui/primitives/button';
import { Campo } from '@/ui/primitives/input';
import { EXTENSOES_CERTIFICADO, LIMITE_ARQUIVO_CERTIFICADO } from '@fiscoai/contracts';

import { useEnviarCertificado } from './api';

interface Props {
  empresaId: string;
  /** Já há certificado: o envio substitui o atual. */
  troca: boolean;
  aoConcluir?: () => void;
  aoCancelar?: () => void;
}

const KB = LIMITE_ARQUIVO_CERTIFICADO / 1024;

/** Por que o botão ainda está desabilitado — dito na tela, não só na cor do botão. */
function oQueFalta(arquivo: File | null, senha: string): string | null {
  if (!arquivo && !senha) return 'Escolha o arquivo do certificado e digite a senha.';
  if (!arquivo) return 'Escolha o arquivo do certificado.';
  if (!senha) return 'Digite a senha do certificado.';
  return null;
}

/**
 * Envio do A1: arquivo + senha, uma única vez. Aceita o arquivo pelo conteúdo, não pela extensão
 * (há A1 exportado como `.cfx` — produção, 14/10/2026): quem valida o PKCS#12 é o servidor. A
 * senha não fica guardada na tela (é limpa ao enviar, com sucesso ou erro) e o arquivo só existe
 * na memória do navegador até o envio. Erro fica escrito na tela até a próxima tentativa.
 */
export function FormularioEnvioCertificado({ empresaId, troca, aoConcluir, aoCancelar }: Props) {
  const enviar = useEnviarCertificado(empresaId);
  const [arquivo, setArquivo] = useState<File | null>(null);
  const [senha, setSenha] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const falta = oQueFalta(arquivo, senha);

  const escolher = (escolhido: File) => {
    setErro(null);
    if (escolhido.size > LIMITE_ARQUIVO_CERTIFICADO) {
      setArquivo(null);
      setErro(`${escolhido.name} passa de ${String(KB)} KB: não parece um certificado A1.`);
      return;
    }
    setArquivo(escolhido);
  };

  const confirmar = () => {
    if (!arquivo || !senha) return;
    setErro(null);
    enviar.mutate(
      { arquivo, senha },
      {
        onSuccess: () => {
          toast.success(troca ? 'Certificado substituído' : 'Certificado guardado com segurança');
          setArquivo(null);
          aoConcluir?.();
        },
        onError: (falha) => {
          setErro(mensagemDeErro(falha));
        },
        onSettled: () => {
          setSenha('');
          enviar.reset();
        },
      },
    );
  };

  return (
    <form
      className="grid gap-4"
      noValidate
      autoComplete="off"
      onSubmit={(e) => {
        e.preventDefault();
        confirmar();
      }}
    >
      <Dropzone
        multiplo={false}
        compacto={arquivo !== null}
        aoSoltar={([escolhido]) => {
          if (escolhido) escolher(escolhido);
        }}
        titulo={arquivo ? arquivo.name : 'Arraste o certificado A1 ou clique para escolher'}
        descricao={
          arquivo
            ? 'Escolha outro para trocar'
            : `Arquivo do A1 (em geral ${EXTENSOES_CERTIFICADO.join(' ou ')}), até ${String(KB)} KB`
        }
      />
      <Campo rotulo="Senha do certificado" ajuda="Usada só para abrir o arquivo; fica cifrada.">
        {(a) => (
          <CampoSenha
            {...a}
            autoComplete="off"
            data-1p-ignore
            data-lpignore="true"
            data-form-type="other"
            value={senha}
            onChange={(e) => {
              setErro(null);
              setSenha(e.target.value);
            }}
          />
        )}
      </Campo>
      {erro && (
        <p role="alert" className="rounded-controle bg-perigo-suave px-3 py-2 text-sm text-perigo">
          {erro}
        </p>
      )}
      <p className="flex items-start gap-2 text-xs text-texto-suave">
        <Lock className="mt-0.5 size-3.5 shrink-0" aria-hidden />
        Depois de enviados, o arquivo e a senha ficam cifrados e nenhuma tela ou relatório os mostra
        ou permite baixar. Só a captura de notas na SEFAZ os usa.
      </p>
      <div className="flex flex-wrap items-center justify-end gap-x-3 gap-y-2">
        {falta && !erro && <span className="text-sm text-texto-suave">{falta}</span>}
        {aoCancelar && (
          <Button variante="fantasma" onClick={aoCancelar}>
            Cancelar
          </Button>
        )}
        <Button
          type="submit"
          variante="primario"
          disabled={falta !== null}
          carregando={enviar.isPending}
        >
          <KeyRound aria-hidden />
          {enviar.isPending ? 'Enviando…' : troca ? 'Substituir certificado' : 'Enviar certificado'}
        </Button>
      </div>
    </form>
  );
}
